import { useQuery } from '@tanstack/react-query'
import { useApi } from './useApi'
import { SupportedChainId } from '../constants/networks'

export interface TimelockItem {
  environment: string
  task: string
  timelock: {
    id: string
    mode: 'Seconds' | 'OnDay'
    allowedAt: string
    frequency: string
    window: string
  }
  chainId?: number
  chainIds?: number[] // For grouped timelocks
}

const useAllTimelocks = () => {
  const { callApi } = useApi()

  const fetchAllTimelocks = async (): Promise<TimelockItem[]> => {
    const chainIds = Object.values(SupportedChainId)
    const allTimelocks: TimelockItem[] = []

    // Fetch timelocks sequentially to avoid overwhelming the server
    for (const chainId of chainIds) {
      try {
        const timelocks = await callApi<TimelockItem[]>(`/relayer-executor/timelocks/${chainId}`)

        if (Array.isArray(timelocks)) {
          // Add chainId to each timelock
          const timelocksWithChain = timelocks.map(tl => ({
            ...tl,
            chainId: chainId
          }))
          allTimelocks.push(...timelocksWithChain)
        }

        // Small delay between requests
        await new Promise(resolve => setTimeout(resolve, 50))
      } catch (error) {
        console.error(`Error fetching timelocks for chain ${chainId}:`, error)
        // Continue with other chains even if one fails
      }
    }

    // Filter timelocks: show those from last 48 hours to next 48 hours
    const now = Math.floor(Date.now() / 1000) // Current time in seconds
    const fortyEightHoursAgo = now - (48 * 60 * 60) // 48 hours ago in seconds
    const fortyEightHoursFromNow = now + (48 * 60 * 60) // 48 hours from now in seconds

    const upcomingTimelocks = allTimelocks.filter(tl => {
      const allowedAt = parseInt(tl.timelock.allowedAt)
      // Show if allowedAt is between (now - 48h) and (now + 48h)
      return allowedAt >= fortyEightHoursAgo && allowedAt <= fortyEightHoursFromNow
    })

    // Group timelocks by environment, task, and allowedAt
    const groupedMap = new Map<string, TimelockItem>()

    upcomingTimelocks.forEach(tl => {
      const key = `${tl.environment}-${tl.task}-${tl.timelock.allowedAt}`
      const existing = groupedMap.get(key)

      if (existing) {
        // Add chainId to the existing group
        if (tl.chainId) {
          if (!existing.chainIds) {
            existing.chainIds = existing.chainId ? [existing.chainId] : []
          }
          if (!existing.chainIds.includes(tl.chainId)) {
            existing.chainIds.push(tl.chainId)
          }
        }
      } else {
        // Create new entry
        groupedMap.set(key, {
          ...tl,
          chainIds: tl.chainId ? [tl.chainId] : []
        })
      }
    })

    // Convert map to array
    const groupedTimelocks = Array.from(groupedMap.values())

    // Sort by allowedAt timestamp (ascending - soonest first)
    const sortedTimelocks = groupedTimelocks.sort((a, b) => {
      const timeA = parseInt(a.timelock.allowedAt)
      const timeB = parseInt(b.timelock.allowedAt)
      return timeA - timeB
    })

    // Return all timelocks within the time range
    return sortedTimelocks
  }

  return useQuery<TimelockItem[]>({
    queryKey: ['allTimelocks'],
    queryFn: fetchAllTimelocks,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes
  })
}

export default useAllTimelocks
